import type { InvoiceDraftResponse } from '../../../shared/invoicing';
import { ProjectInvoiceAction } from '../../components/ProjectMoneyStrip';
import { NewInvoiceModal } from '../Finance';
import { DeliverablesPanel } from '../../components/DeliverablesPanel';
import { WrapNotesCard } from '../../components/WrapNotesCard';
import { ProjectMeetings } from '../../components/ProjectMeetings';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { reportError } from '../../lib/errorEvents';
import { melbourneToday } from '../../../shared/dates';
import { bookingCostCents } from '../../../shared/crewCost';
import { useMoneyMode } from '../../lib/money';
import { CREATE_TOOL_ROUTES, crewBookedDays, DASHBOARD_BASE, parseHalfDayDates, pillTone, STEP_LABELS, STEPS } from '../../lib/projectDetailShared';
import { useIsMobile } from '../../lib/useIsMobile';
import { ProjectDetailMobile } from '../../components/ProjectDetailMobile';
import type { ResolvedProjectContact } from '../../../shared/clientContact';
import { PHASE_LABELS, type ClientContactRow, type ClientWithCounts, type CrewContactRow, type InvoiceRow, type ProjectArtifactRow, type ProjectCrewConflict, type ProjectCrewWithName, type ProjectDeliverableRow, type ProjectPhase, type TaskRow as TaskRowType, type UnlinkedArtifactCandidate } from '../../../shared/types';
import { addDays, Dropdown, formatDayMonth, Group, Modal, NotesBlock, Pill, Timeline, TimelineRow } from '../../components/primitives';
import { QuickAddRow, TaskRow } from '../../components/TaskRow';
import { TemplateModal, type TaskTemplate } from '../Settings';
import { NewTaskModal } from '../Tasks';
import type { ProjectDetailRow, ActivityEntry, ProjectCallsheetData } from './types';
import type { ProjectWrapNotes } from '../../../shared/types';
import { tlDate } from './helpers';
import { ProjectNumberEditor } from './ProjectNumberEditor';
import { ProjectRailBody } from './ProjectRail';
import { NewArtifactModal } from './NewArtifactModal';
import { CutApprovedModal } from './CutApprovedModal';
import { CloseProjectModal } from '../../components/CloseProjectModal';

// Project detail per mockups/03-project-detail.html: breadcrumb header with
// project number + phase pill + actions, clickable phase stepper (= phase
// PATCH), tasks block with quick-add, autosaving notes, timeline, and the
// money/archive/social rail. Inline editing everywhere.

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<ProjectDetailRow | null>(null);
  const [tasks, setTasks] = useState<TaskRowType[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [wrapNotes, setWrapNotes] = useState<ProjectWrapNotes[]>([]);
  const [artifacts, setArtifacts] = useState<ProjectArtifactRow[]>([]);
  const retainReviewArtifact = (artifact: ProjectArtifactRow) => {
    setArtifacts((rows) => rows.some((row) => row.id === artifact.id)
      ? rows.map((row) => row.id === artifact.id ? artifact : row) : [...rows, artifact]);
    refresh();
  };
  const [unlinked, setUnlinked] = useState<UnlinkedArtifactCandidate[]>([]);
  // Whether each create-in-context service binding + secret is configured
  // on this worker; disables the matching "New" action
  // instead of only failing after the click (mirrors the Dashboards module).
  const [toolIntegrations, setToolIntegrations] = useState({ scripts: false, callsheets: false, reviews: false });
  const [createModal, setCreateModal] = useState<'script' | 'callsheet' | 'review' | null>(null);
  const [missing, setMissing] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [clients, setClients] = useState<ClientWithCounts[] | null>(null);
  const [addSignal, setAddSignal] = useState(0);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [tmplOpen, setTmplOpen] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [waitingBusy, setWaitingBusy] = useState(false);
  const [waitingNote, setWaitingNote] = useState<string | null>(null);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [cutApprovedOpen, setCutApprovedOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [invoiceDraft, setInvoiceDraft] = useState<InvoiceDraftResponse | null>(null);
  const [newInvoiceDefaults, setNewInvoiceDefaults] = useState<InvoiceDraftResponse | null>(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const draftBusyRef = useRef(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  // Delivery links (Phase 3.2 — payment-gated delivery page).
  const [deliverables, setDeliverables] = useState<ProjectDeliverableRow[]>([]);
  const [deliveryLinkBusy, setDeliveryLinkBusy] = useState(false);
  const [deliveryLinkNote, setDeliveryLinkNote] = useState<string | null>(null);
  // "On this job" crew block (Phase 3.3) — bookings + the register (for the
  // add-flow's crew picker). Conflicts come back on each create/patch
  // response, not on list, so they're held separately per booking id and
  // only reflect the LATEST save (mockup #4 — checked on add/edit).
  const [crewBookings, setCrewBookings] = useState<ProjectCrewWithName[]>([]);
  const [crewRegister, setCrewRegister] = useState<CrewContactRow[]>([]);
  const [crewConflicts, setCrewConflicts] = useState<Record<string, ProjectCrewConflict[]>>({});
  // Multi-contacts (mockups-feature-builds §3b) — the client's live contact
  // register + the picked → override → default resolution, both returned
  // alongside project on every GET so they're never stale relative to it.
  const [clientContacts, setClientContacts] = useState<ClientContactRow[]>([]);
  const [resolvedContact, setResolvedContact] = useState<ResolvedProjectContact>({
    name: null,
    email: null,
    phone: null,
    source: 'client_default',
  });
  // Mobile (Phase B §6): the "···" sheet's New task, and the signal that
  // opens the project-number field sheet.
  const [showNewTask, setShowNewTask] = useState(false);
  const [numberSignal, setNumberSignal] = useState(0);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const today = melbourneToday();
  const mode = useMoneyMode();

  // A stale response from an earlier refresh must not repaint newer state;
  // `refresh` also cancels a pending coalesced `ss:mutated` fetch so the
  // page's own PATCH costs one round trip (same contract as Tasks/Leads).
  const fetchSeq = useRef(0);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    const seq = ++fetchSeq.current;
    api<InvoiceDraftResponse>(`/api/admin/projects/${id}/invoice-draft`)
      .then(d => { if (seq === fetchSeq.current) { setInvoiceDraft(d); setDraftError(null); } })
      .catch(() => { if (seq === fetchSeq.current) { setInvoiceDraft(null); setDraftError('Could not load invoicing.'); } });
    api<{
      project: ProjectDetailRow;
      resolved_contact: ResolvedProjectContact;
      client_contacts: ClientContactRow[];
      tasks: TaskRowType[];
      invoices: InvoiceRow[];
      artifacts: ProjectArtifactRow[];
      unlinked: UnlinkedArtifactCandidate[];
      tool_integrations: { scripts: boolean; callsheets: boolean; reviews: boolean };
    } & ProjectCallsheetData>(`/api/admin/projects/${id}`)
      .then((d) => {
        if (seq !== fetchSeq.current) return;
        setProject(d.project);
        setResolvedContact(d.resolved_contact);
        setClientContacts(d.client_contacts ?? []);
        setTasks(d.tasks);
        setInvoices(d.invoices);
        setActivity(d.activity);
        setWrapNotes(d.wrap_notes ?? []);
        setArtifacts(d.artifacts);
        setUnlinked(d.unlinked);
        setToolIntegrations(d.tool_integrations);
      })
      .catch(() => { if (seq === fetchSeq.current) setMissing(true); });
    api<{ templates: TaskTemplate[] }>('/api/admin/task-templates')
      .then((d) => { if (seq === fetchSeq.current) setTemplates(d.templates); })
      .catch(() => {});
    api<{ deliverables: ProjectDeliverableRow[] }>(`/api/admin/projects/${id}/deliverables`)
      .then((d) => { if (seq === fetchSeq.current) setDeliverables(d.deliverables); })
      .catch(() => {});
  }, [id]);
  const draftRequest = useRef(0);
  useEffect(() => {
    setInvoiceDraft(null); setNewInvoiceDefaults(null); setDraftError(null); setDraftBusy(false);
    return () => { draftRequest.current++; draftBusyRef.current = false; };
  }, [id]);
  const openProjectDraft = async () => {
    if (draftBusyRef.current) return;
    draftBusyRef.current = true;
    const request = ++draftRequest.current;
    setDraftBusy(true); setDraftError(null);
    try {
      const d = await api<InvoiceDraftResponse>(`/api/admin/projects/${id}/invoice-draft`);
      if (request !== draftRequest.current) return;
      setInvoiceDraft(d);
      if (d.reason === 'free_project') return;
      if (d.invoiced.open_draft) { navigate(`/finance/invoices/${d.invoiced.open_draft.id}`); return; }
      if (d.strip.action?.type === 'draft') setNewInvoiceDefaults(d);
    } catch (err) {
      if (request === draftRequest.current) setDraftError(err instanceof Error ? err.message : 'Could not load the draft.');
    } finally {
      if (request === draftRequest.current) { draftBusyRef.current = false; setDraftBusy(false); }
    }
  };
  useEffect(refresh, [refresh]);
  // Mutations made elsewhere (the quick-add sheet's "+", a task sheet) arrive
  // as `ss:mutated` and are coalesced 250ms, as on Tasks — so a task added
  // from the FAB shows up in this project's list without navigating. The
  // page's own writes still call refresh() directly.
  useEffect(() => {
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(refresh, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = null;
      fetchSeq.current++;
    };
  }, [refresh]);

  /** Loads the client list once — behind the desktop dropdown and the mobile
      picker sheet alike. */
  const ensureClients = useCallback(() => {
    if (clients !== null) return;
    setClients([]);
    api<{ clients: ClientWithCounts[] }>('/api/admin/clients')
      .then((data) => setClients(data.clients))
      .catch((error) => {
        setClients(null);
        reportError(error, 'Could not load clients.');
      });
  }, [clients]);

  const openClientPicker = () => {
    const next = !clientOpen;
    setClientOpen(next);
    if (next) ensureClients();
  };

  // Delivery link (Phase 3.2) — generates the project's public delivery
  // token on demand, same idiom as ClientDetail's "Copy preferences link".
  const copyDeliveryLink = async () => {
    if (!project || deliveryLinkBusy) return;
    setDeliveryLinkBusy(true);
    setDeliveryLinkNote(null);
    try {
      const result = await api<{ url: string }>(`/api/admin/projects/${project.id}/delivery-link`, { method: 'POST' });
      await navigator.clipboard.writeText(result.url);
      setDeliveryLinkNote('Copied!');
    } catch (err) {
      setDeliveryLinkNote(err instanceof Error ? err.message : 'Failed to create link.');
    } finally {
      setDeliveryLinkBusy(false);
      setTimeout(() => setDeliveryLinkNote(null), 4000);
    }
  };

  const refreshCrew = useCallback(() => {
    if (!id) return;
    api<{ crew: ProjectCrewWithName[] }>(`/api/admin/projects/${id}/crew`)
      .then((d) => setCrewBookings(d.crew))
      .catch(() => {});
  }, [id]);
  useEffect(refreshCrew, [refreshCrew]);
  useEffect(() => {
    api<{ crew: CrewContactRow[] }>('/api/admin/crew')
      .then((d) => setCrewRegister(d.crew))
      .catch(() => {});
  }, []);

  const addCrewBooking = async (fields: {
    crew_id: string;
    role: string | null;
    day_rate_cents: number | null;
    start_date: string;
    end_date: string;
    half_day_dates: string[];
    half_day_rate_cents: number | null;
  }) => {
    if (!project) return;
    const res = await api<{ booking: ProjectCrewWithName; conflicts: ProjectCrewConflict[] }>(
      `/api/admin/projects/${project.id}/crew`,
      { method: 'POST', json: fields },
    );
    setCrewConflicts((prev) => ({ ...prev, [res.booking.id]: res.conflicts }));
    refreshCrew();
  };

  const patchCrewBooking = async (bookingId: string, fields: Record<string, unknown>) => {
    if (!project) return;
    const res = await api<{ booking: ProjectCrewWithName; conflicts: ProjectCrewConflict[] }>(
      `/api/admin/projects/${project.id}/crew/${bookingId}`,
      { method: 'PATCH', json: fields },
    );
    setCrewConflicts((prev) => ({ ...prev, [bookingId]: res.conflicts }));
    refreshCrew();
  };

  const removeCrewBooking = async (bookingId: string) => {
    if (!project) return;
    await api(`/api/admin/projects/${project.id}/crew/${bookingId}`, { method: 'DELETE' })
      .catch((error) => reportError(error, 'Could not remove the crew booking.'));
    setCrewConflicts((prev) => {
      const next = { ...prev };
      delete next[bookingId];
      return next;
    });
    refreshCrew();
  };

  const unlinkArtifact = async (artifactId: string) => {
    if (!project) return;
    await api(`/api/admin/projects/${project.id}/artifacts/${artifactId}`, { method: 'DELETE' })
      .catch((error) => reportError(error, 'Could not unlink the artifact.'));
    refresh();
  };

  const adoptArtifact = async (candidate: UnlinkedArtifactCandidate) => {
    if (!project) return;
    await api(`/api/admin/projects/${project.id}/artifacts`, {
      method: 'POST',
      json: {
        tool: candidate.tool,
        external_id: candidate.external_id,
        title: candidate.title,
        public_url: candidate.url,
        status: candidate.status,
      },
    }).catch((error) => reportError(error, 'Could not link the artifact.'));
    refresh();
  };

  const applyTemplate = async (templateId: string) => {
    await api(`/api/admin/task-templates/${templateId}/apply`, {
      method: 'POST',
      json: { project_id: id },
    }).catch((error) => reportError(error, 'Could not apply the task template.'));
    refresh();
  };

  const patch = async (fields: Record<string, unknown>) => {
    if (!project) return;
    // Delivered/Clean up → Closed always goes through backup confirmation,
    // whichever control asked for it (stepper, rail picker, phone sheet).
    if (fields.phase === 'closed' && ['delivered', 'clean_up'].includes(project.phase)) {
      setCloseOpen(true);
      return;
    }
    const prev = project;
    setProject({ ...project, ...fields } as ProjectDetailRow);
    try {
      await api(`/api/admin/projects/${project.id}`, { method: 'PATCH', json: fields });
      refresh();
    } catch (error) {
      setProject(prev);
      reportError(error, 'Could not save the project change.');
    }
  };

  // Testimonial ask control (Phase 2.3): close it ("mark responded"/"don't
  // ask") or reopen — the nightly cron reads these stamps.
  const testimonialAction = async (action: 'responded' | 'skip' | 'reopen') => {
    if (!project) return;
    await api(`/api/admin/projects/${project.id}/testimonial`, { method: 'POST', json: { action } })
      .catch((error) => reportError(error, 'Could not update the testimonial status.'));
    refresh();
  };

  const waitingSendNow = async () => {
    if (!project) return;
    setWaitingBusy(true);
    setWaitingNote(null);
    try {
      await api(`/api/admin/projects/${project.id}/waiting-nudge-now`, { method: 'POST' });
      setWaitingNote('Sent!');
      refresh();
    } catch (err) {
      setWaitingNote(err instanceof ApiError ? err.message : 'Failed to send.');
    } finally {
      setWaitingBusy(false);
      setTimeout(() => setWaitingNote(null), 6000);
    }
  };

  const waitingSnooze = async () => {
    if (!project) return;
    setWaitingBusy(true);
    await patch({ waiting_snoozed_until: addDays(today, 7) });
    setWaitingBusy(false);
  };

  const changeVoidedState = async () => {
    if (!project || voidBusy) return;
    setVoidBusy(true);
    setVoidError(null);
    const action = project.voided_at === null ? 'void' : 'unvoid';
    try {
      const result = await api<{ project: ProjectDetailRow }>(
        `/api/admin/projects/${project.id}/${action}`,
        { method: 'POST' },
      );
      setProject(result.project);
      setVoidConfirmOpen(false);
      refresh();
    } catch (err) {
      setVoidError(err instanceof Error ? err.message : `Could not ${action} project.`);
    } finally {
      setVoidBusy(false);
    }
  };

  if (missing) {
    return (
      <div className="page-body">
        <div className="content">
          <div className="empty-note">Project not found.</div>
        </div>
      </div>
    );
  }
  if (!project) return <div className="page-body" />;

  const open = tasks.filter((t) => t.status !== 'done');
  const done = tasks.filter((t) => t.status === 'done');
  const dashboardSlug = project.dashboard_slug ?? project.client_dashboard_slug ?? null;
  const stepIndex = STEPS.indexOf(project.phase);
  // Value is an ex-GST quote figure (Henry, 2026-06-13); the invoice
  // fallback strips GST so the field never mixes semantics.
  const moneyTotal =
    project.value_cents ??
    (invoices.length ? invoices.reduce((s, i) => s + i.amount_cents - i.gst_cents, 0) : null);

  // "On this job" crew totals (Phase 3.3 + half-day rates, mockup §4c) —
  // each booking's cost comes from the shared bookingCostCents split (full
  // days at day_rate_cents, half days at half_day_rate_cents); the day
  // count is still a union across entries (a two-person, two-day shoot is
  // still a "2-day shoot", not 4), but cost and half count are per-booking
  // sums — you pay every crew member separately even on an overlapping day.
  const crewCosts = crewBookings.map((b) => bookingCostCents(b));
  const crewTotalCents = crewCosts.some((c) => c === null)
    ? null
    : crewCosts.reduce((sum, c) => sum + (c as { total_cents: number }).total_cents, 0);
  const crewPeopleCount = new Set(crewBookings.map((b) => b.crew_id)).size;
  const crewDayCount = crewBookedDays(crewBookings);
  const crewHalfCount = crewBookings.reduce((sum, b) => sum + parseHalfDayDates(b.half_day_dates).length, 0);

  const stepDate = (phase: ProjectPhase): string => {
    if (phase === project.phase) return 'now';
    switch (phase) {
      case 'lead':
        return project.start_date ? formatDayMonth(project.start_date) : '';
      case 'shoot':
        return project.shoot_date ? formatDayMonth(project.shoot_date) : '';
      case 'post':
        return project.edit_date ? formatDayMonth(project.edit_date) : '';
      case 'delivered':
        return project.delivered_date
          ? formatDayMonth(project.delivered_date)
          : project.due_date
            ? `due ${formatDayMonth(project.due_date)}`
            : '';
      default:
        return '';
    }
  };

  const subBits = [
    project.client_name,
    project.due_date ? `due ${formatDayMonth(project.due_date)}` : null,
    `${open.length} open task${open.length === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return (
    <>
      {isMobile ? (
        /* ≤880px (Phase B §6): the whole page is the mobile component; the
           two-column .detail-grid and ProjectRailBody are not rendered.
           Every handler and modal below is shared with the desktop render. */
        <ProjectDetailMobile
          invoiceDraft={project.voided_at === null ? invoiceDraft : null}
          invoiceAction={invoiceDraft && project.voided_at === null ? <ProjectInvoiceAction action={invoiceDraft.strip.action} onDraft={openProjectDraft} busy={draftBusy} mobile /> : null}
          project={project}
          today={today}
          mode={mode}
          dashboardSlug={dashboardSlug}
          moneyTotal={moneyTotal}
          stepDate={stepDate}
          subBits={subBits as string[]}
          openTasks={open}
          doneTasks={done}
          showDone={showDone}
          setShowDone={setShowDone}
          addSignal={addSignal}
          templates={templates}
          applyTemplate={applyTemplate}
          onNewTask={() => setShowNewTask(true)}
          onSaveTemplate={() => setShowSaveTemplate(true)}
          activity={activity}
          wrapNotes={wrapNotes}
          tlDate={tlDate}
          invoices={invoices}
          clients={clients ?? []}
          ensureClients={ensureClients}
          clientContacts={clientContacts}
          resolvedContact={resolvedContact}
          crewBookings={crewBookings}
          crewRegister={crewRegister}
          crewConflicts={crewConflicts}
          crewTotalCents={crewTotalCents}
          crewPeopleCount={crewPeopleCount}
          crewDayCount={crewDayCount}
          crewHalfCount={crewHalfCount}
          artifacts={artifacts}
          onReviewArtifactUpdated={retainReviewArtifact}
          unlinked={unlinked}
          toolIntegrations={toolIntegrations}
          deliverables={deliverables}
          deliveryLinkBusy={deliveryLinkBusy}
          deliveryLinkNote={deliveryLinkNote}
          waitingBusy={waitingBusy}
          waitingNote={waitingNote}
          refresh={refresh}
          patch={patch}
          waitingSendNow={waitingSendNow}
          waitingSnooze={waitingSnooze}
          testimonialAction={testimonialAction}
          patchCrewBooking={patchCrewBooking}
          removeCrewBooking={removeCrewBooking}
          addCrewBooking={addCrewBooking}
          copyDeliveryLink={copyDeliveryLink}
          unlinkArtifact={unlinkArtifact}
          adoptArtifact={adoptArtifact}
          onCreateArtifact={setCreateModal}
          onCutApproved={() => setCutApprovedOpen(true)}
          onCloseProject={() => setCloseOpen(true)}
          onVoid={() => {
            setVoidError(null);
            setVoidConfirmOpen(true);
          }}
          onChangeNumber={() => setNumberSignal((n) => n + 1)}
          onTrashProject={async (cascade) => {
            await api(`/api/admin/projects/${project.id}${cascade ? '?invoices=1' : ''}`, {
              method: 'DELETE',
            });
            navigate('/projects');
          }}
        />
      ) : (
        <>
      <header className="detail-header">
        <Link className="crumb" to="/projects">
          Projects
        </Link>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">
          <ProjectNumberEditor
            project={project}
            onSaved={(savedProject) => {
              setProject(savedProject);
              refresh();
            }}
          />
          {/* Unnumbered projects read as just the name — the editor's own "—"
              placeholder stays clickable, but without a dangling separator. */}
          {project.project_number === null ? ' ' : ' · '}
          {project.name}
        </span>
        <span style={{ marginLeft: 6 }}>
          <Pill tone={pillTone(project.phase)}>{PHASE_LABELS[project.phase]}</Pill>
        </span>
        {project.voided_at !== null && (
          <span style={{ marginLeft: 6 }}>
            <Pill tone="danger">Voided</Pill>
          </span>
        )}
        <div className="detail-actions">
          <button
            className={`btn${project.voided_at === null ? ' danger' : ''}`}
            onClick={() => {
              setVoidError(null);
              setVoidConfirmOpen(true);
            }}
          >
            {project.voided_at === null ? 'Void project' : 'Unvoid project'}
          </button>
          <button className="btn" onClick={() => setAddSignal((n) => n + 1)}>
            + Task
          </button>
          {dashboardSlug && (
            <a className="btn" href={`${DASHBOARD_BASE}/${dashboardSlug}`} target="_blank" rel="noreferrer">
              Client dashboard ↗
            </a>
          )}
          {project.phase === 'post' ? (
            // Cut approved (Phase 3.1, mockup #5) replaces "Mark delivered"
            // while the project is in Post — the modal's phase-advance
            // checkbox covers the same effect, plus follow-up tasks and a
            // draft invoice in one confirmable step.
            // Voided projects fire no automation server-side, so the button
            // stays visible but inert rather than lying about what it'll do.
            <button
              className="btn primary"
              onClick={() => setCutApprovedOpen(true)}
              disabled={project.voided_at !== null}
              title={project.voided_at !== null ? 'Unvoid the project first' : undefined}
            >
              Cut approved →
            </button>
          ) : project.phase === 'delivered' ? (
            <button className="btn primary" onClick={() => patch({ phase: 'clean_up' })}>
              Move to clean up →
            </button>
          ) : project.phase === 'clean_up' ? (
            <button className="btn primary" onClick={() => setCloseOpen(true)}>
              Close project →
            </button>
          ) : (
            project.phase !== 'closed' && (
              <button className="btn primary" onClick={() => patch({ phase: 'delivered' })}>
                Mark delivered →
              </button>
            )
          )}
        </div>
      </header>

      <div className="page-body">
        <div className="detail-grid">
          <div className="detail-main">
            <h1 className="detail-name">{project.name}</h1>
            <div className="detail-sub">{subBits.join(' · ')}</div>

            <div className="stepper">
              {STEPS.map((phase, i) => {
                const cls =
                  stepIndex >= 0 && i < stepIndex ? ' done' : phase === project.phase ? ' current' : '';
                return (
                  <div key={phase} className={`step${cls}${phase === 'clean_up' ? ' step-cleanup' : ''}`} onClick={() => patch({ phase })}>
                    <span className="step-dot" />
                    <span className="step-label">{STEP_LABELS[phase]}</span>
                    <span className="step-date">{stepDate(phase)}</span>
                  </div>
                );
              })}
            </div>

            <Group
              title="Tasks"
              count={`${open.length} open`}
              link={
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'baseline' }}>
                  {templates.length > 0 && (
                    <span className="dropdown-anchor">
                      <a
                        className="group-link"
                        style={{ cursor: 'pointer', marginLeft: 0 }}
                        onClick={() => setTmplOpen((o) => !o)}
                      >
                        + From template
                      </a>
                      {tmplOpen && (
                        <Dropdown
                          items={templates.map((t) => ({
                            value: t.id,
                            label: `${t.name} · ${t.items.length}`,
                          }))}
                          onSelect={applyTemplate}
                          onClose={() => setTmplOpen(false)}
                        />
                      )}
                    </span>
                  )}
                  {tasks.length > 0 && (
                    <a
                      className="group-link"
                      style={{ cursor: 'pointer', marginLeft: 0 }}
                      onClick={() => setShowSaveTemplate(true)}
                    >
                      Save as template
                    </a>
                  )}
                  {done.length > 0 && (
                    <a
                      className="group-link"
                      style={{ cursor: 'pointer', marginLeft: 0 }}
                      onClick={() => setShowDone((s) => !s)}
                    >
                      {showDone ? 'Hide done' : 'Show done →'}
                    </a>
                  )}
                </span>
              }
            >
              {open.map((t) => (
                <TaskRow key={t.id} task={t} today={today} projectDisplay="none" onChanged={refresh} />
              ))}
              {showDone &&
                done.map((t) => (
                  <div key={t.id} style={{ opacity: 0.55 }}>
                    <TaskRow task={t} today={today} projectDisplay="none" onChanged={refresh} />
                  </div>
                ))}
              <QuickAddRow
                hint="Add task to this project…"
                defaults={{ project_id: project.id, due_date: today }}
                onAdded={refresh}
                openSignal={addSignal}
              />
            </Group>

            <WrapNotesCard notes={wrapNotes} />

            <DeliverablesPanel projectNumber={project.project_number} projectName={project.name} key={project.id} projectId={project.id} deliverables={deliverables} artifacts={artifacts} onChanged={refresh} copyDeliveryLink={copyDeliveryLink} deliveryLinkBusy={deliveryLinkBusy} deliveryLinkNote={deliveryLinkNote} />

            <ProjectMeetings key={`meetings-${project.id}`} projectId={project.id} />

            <NotesBlock notes={project.summary} onSave={(summary) => patch({ summary })} />

            <Group title="Timeline">
              <Timeline>
                {activity.map((a) => (
                  <TimelineRow key={a.id} date={tlDate(a.created_at)} callsheetUrl={a.callsheet_url}>
                    {a.summary}
                  </TimelineRow>
                ))}
                <TimelineRow date={tlDate(project.created_at)}>Project created</TimelineRow>
              </Timeline>
            </Group>
          </div>

          <ProjectRailBody
            invoiceDraft={project.voided_at === null ? invoiceDraft : null}
            invoiceAction={invoiceDraft && project.voided_at === null && invoiceDraft.strip.action ? <ProjectInvoiceAction action={invoiceDraft.strip.action} onDraft={openProjectDraft} busy={draftBusy} /> : null}
            project={project}
            dashboardSlug={dashboardSlug}
            today={today}
            mode={mode}
            moneyTotal={moneyTotal}
            invoices={invoices}
            clientContacts={clientContacts}
            resolvedContact={resolvedContact}
            clients={clients ?? []}
            clientOpen={clientOpen}
            setClientOpen={setClientOpen}
            openClientPicker={openClientPicker}
            crewBookings={crewBookings}
            crewRegister={crewRegister}
            crewConflicts={crewConflicts}
            crewTotalCents={crewTotalCents}
            crewPeopleCount={crewPeopleCount}
            crewDayCount={crewDayCount}
            crewHalfCount={crewHalfCount}
            deliverables={deliverables}
            artifacts={artifacts}
            onReviewArtifactUpdated={retainReviewArtifact}
            unlinked={unlinked}
            toolIntegrations={toolIntegrations}
            phaseOpen={phaseOpen}
            setPhaseOpen={setPhaseOpen}
            socialOpen={socialOpen}
            setSocialOpen={setSocialOpen}
            waitingBusy={waitingBusy}
            waitingNote={waitingNote}
            deliveryLinkBusy={deliveryLinkBusy}
            deliveryLinkNote={deliveryLinkNote}
            setCreateModal={setCreateModal}
            patch={patch}
            waitingSendNow={waitingSendNow}
            waitingSnooze={waitingSnooze}
            testimonialAction={testimonialAction}
            patchCrewBooking={patchCrewBooking}
            removeCrewBooking={removeCrewBooking}
            addCrewBooking={addCrewBooking}
            copyDeliveryLink={copyDeliveryLink}
            unlinkArtifact={unlinkArtifact}
            adoptArtifact={adoptArtifact}
            onTrashProject={async (cascade) => {
              await api(`/api/admin/projects/${project.id}${cascade ? '?invoices=1' : ''}`, {
                method: 'DELETE',
              });
              navigate('/projects');
            }}
          />
        </div>
      </div>
        </>
      )}

      {/* The mobile number editor renders nothing until "···" → Change
          project number bumps its signal (§6.1 C). */}
      {isMobile && (
        <ProjectNumberEditor
          project={project}
          onSaved={(savedProject) => {
            setProject(savedProject);
            refresh();
          }}
          mobile
          openSignal={numberSignal}
        />
      )}
      {showNewTask && (
        <NewTaskModal
          projects={[{ id: project.id, name: project.name, phase: project.phase }]}
          defaultProjectId={project.id}
          onClose={() => setShowNewTask(false)}
          onCreated={refresh}
        />
      )}

      {showSaveTemplate && (
        <TemplateModal
          initial={null}
          seedName={project.name}
          seedItems={[...open, ...done].map((t) => ({ title: t.title, priority: t.priority, notes: t.notes }))}
          onClose={() => setShowSaveTemplate(false)}
          onSaved={() =>
            api<{ templates: TaskTemplate[] }>('/api/admin/task-templates')
              .then((d) => setTemplates(d.templates))
              .catch(() => {})
          }
        />
      )}
      {createModal && (
        <NewArtifactModal
          tool={createModal}
          projectId={project.id}
          seedTitle={project.name}
          seedShootDate={project.shoot_date}
          dashboardSlug={dashboardSlug}
          onRetained={retainReviewArtifact}
          onClose={() => setCreateModal(null)}
          onCreated={(artifact) => {
            refresh();
            navigate(
              `/tools/${CREATE_TOOL_ROUTES[createModal]}?url=${encodeURIComponent(artifact.admin_url ?? '')}`,
            );
          }}
        />
      )}
      {draftError && project.voided_at === null && <p className="error-text" role="alert">{draftError} <button className="btn" onClick={refresh}>Retry</button></p>}
      {newInvoiceDefaults && <NewInvoiceModal draftDefaults={newInvoiceDefaults} onClose={() => setNewInvoiceDefaults(null)} onCreated={refresh} />}
      {closeOpen && (
        <CloseProjectModal
          project={project}
          onClose={() => setCloseOpen(false)}
          onClosed={(closed) => {
            setCloseOpen(false);
            setProject((prev) => (prev ? { ...prev, ...closed } : prev));
            refresh();
          }}
        />
      )}
      {cutApprovedOpen && (
        <CutApprovedModal
          projectId={project.id}
          onClose={() => setCutApprovedOpen(false)}
          onApproved={() => {
            setCutApprovedOpen(false);
            refresh();
          }}
        />
      )}
      {voidConfirmOpen && (
        <Modal
          title={project.voided_at === null ? 'Void project?' : 'Unvoid project?'}
          context={project.name}
          width={520}
          mobile="half"
          onClose={() => !voidBusy && setVoidConfirmOpen(false)}
          foot={
            <>
              <button className="btn" disabled={voidBusy} onClick={() => setVoidConfirmOpen(false)}>
                Cancel
              </button>
              <button
                className={`btn ${project.voided_at === null ? 'danger' : 'primary'}`}
                disabled={voidBusy}
                onClick={changeVoidedState}
              >
                {voidBusy
                  ? 'Saving…'
                  : project.voided_at === null
                    ? 'Void project'
                    : 'Unvoid project'}
              </button>
            </>
          }
        >
          {project.voided_at === null ? (
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              This project will disappear from active lists, the overview, and notifications. Its unpaid invoices
              will stop counting as outstanding, but every invoice remains live and payable. Any later payment will
              still be recorded and counted as revenue.
            </p>
          ) : (
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              This project will return to active lists and notifications in its current phase. Its unpaid invoices
              will count as outstanding again.
            </p>
          )}
          {voidError && <div className="error-text" style={{ marginTop: 12 }}>{voidError}</div>}
        </Modal>
      )}
    </>
  );
}
